import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import axiosInstance from 'ns_libs/nsAxios';
import API_ENDPOINTS, { QUERY_KEYS } from 'services/urls';
import useToast from 'hooks/useToast';

export interface IUpdateProFormaSubprojectLinkParams {
    dealId: number;
    proFormaId: number;
    /** Subproject correlation id to link, or null to unlink. */
    subprojectUuid: string | null;
}

const updateProFormaSubprojectLink = async ({ dealId, proFormaId, subprojectUuid }: IUpdateProFormaSubprojectLinkParams) => {
    const response = await axiosInstance.patch(
        API_ENDPOINTS.PRO_FORMA_SUBPROJECT_LINK(dealId, proFormaId),
        { subprojectUuid },
    );
    return response.data;
};

export const getServerErrorDetail = (error: unknown): string | undefined => {
    if (error instanceof AxiosError && typeof error.response?.data?.detail === 'string') {
        return error.response.data.detail;
    }
    return undefined;
};

export const useUpdateProFormaSubprojectLink = () => {
    const queryClient = useQueryClient();
    const { showError } = useToast();

    return useMutation({
        mutationFn: updateProFormaSubprojectLink,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getProFormas'] });
            queryClient.invalidateQueries({ queryKey: ['proFormaData'] });
            queryClient.invalidateQueries({ queryKey: ['userSubprojects'] });
            queryClient.invalidateQueries({ queryKey: ['organization-projects'] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DEVELOPMENT_PROJECTS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DEALS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DEAL_SUBPROJECTS] });
        },
        onError: (error: unknown) => {
            showError(getServerErrorDetail(error) ?? 'Failed to update the project link. Please try again.');
        },
    });
};

export default useUpdateProFormaSubprojectLink;
